import { useOpenBillingPortal } from '@/hooks/actions/use-open-billing-portal';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { useBillingSettingsQuery } from '@/hooks/queries/use-billing-settings-query';
import { useShopifyIntegrationQuery } from '@/hooks/queries/use-integrations-query';
import { readBillingContext } from '@/libs/billing-access';
import { formatCents } from '@/libs/utils/format-money';
import { formatSettingsDate } from '@/pages/settings/components/format-settings-date';
import type { ShopifyMetadata } from '@/types/integration';
import { Flex, Stack, Text } from '@chakra-ui/react';
import { NoticePanel, ThemedButton } from '@fluenceur/ui';
import type { OrganizationBillingPaymentRecovery } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

function stripeRecoveryDescription(
  recovery: OrganizationBillingPaymentRecovery,
  canManage: boolean
) {
  if (recovery.disputes?.length)
    return canManage
      ? 'settings.subscription.paymentRecovery.disputeDescription'
      : 'settings.subscription.paymentRecovery.disputeMember';
  if (recovery.status === 'retrying')
    return canManage
      ? 'settings.subscription.paymentRecovery.retrying'
      : 'settings.subscription.paymentRecovery.retryingMember';
  return canManage
    ? 'settings.subscription.paymentFailure.stripeDescription'
    : 'settings.subscription.paymentRecovery.unpaidMember';
}

/** Native unpaid invoices remain visible after access has moved to the Free plan. */
export function PaymentFailureBanner() {
  const { t } = useTranslation();
  const billing = useBillingContextQuery();
  const context = readBillingContext(billing.data);
  const settings = useBillingSettingsQuery({
    context: context ?? undefined,
    enabled: context?.channel === 'stripe',
    pollPaymentRecovery: true,
  });
  const portal = useOpenBillingPortal(settings.data);
  const { data: shopifyIntegration } = useShopifyIntegrationQuery();

  if (!context || !['stripe', 'shopify'].includes(context.channel)) return null;

  const stripe = context.channel === 'stripe';
  const snapshot =
    settings.data?.organizationId === context.organizationId &&
    settings.data.brandId === context.brandId
      ? settings.data
      : undefined;
  const recovery = stripe ? snapshot?.paymentRecovery : null;
  const hasDisputeDebt = !!recovery?.disputes?.length;
  if (
    stripe
      ? !recovery
      : !billing.isSuccess || billing.error || !context.subscription?.isInGracePeriod
  )
    return null;
  const canManage = stripe
    ? !!snapshot?.canManage
    : context.capabilities.canCommitPaidPlan && context.cardSetup.exemption !== 'impersonation';
  const canOpen =
    canManage &&
    (!stripe ||
      (settings.isSuccess &&
        !settings.error &&
        !billing.error &&
        snapshot?.portalAvailable &&
        (!hasDisputeDebt || !!recovery?.invoices.length)));
  const canContactSupport =
    hasDisputeDebt && canManage && settings.isSuccess && !settings.error && !billing.error;
  const body =
    stripe && settings.error && !hasDisputeDebt
      ? 'settings.subscription.paymentFailure.message'
      : stripe && recovery
        ? stripeRecoveryDescription(recovery, canManage)
        : canManage
          ? 'settings.subscription.paymentFailure.shopifyDescription'
          : 'settings.subscription.paymentFailure.memberDescription';
  const openBilling = () => {
    if (!canOpen) return;
    if (stripe) {
      void portal.mutateAsync().catch(() => undefined);
      return;
    }
    const metadata = shopifyIntegration?.metadata as ShopifyMetadata | undefined;
    const billingUrl = metadata?.shopDomain
      ? `https://${metadata.shopDomain}/admin/settings/billing`
      : 'https://admin.shopify.com/settings/billing';
    window.open(billingUrl, '_blank', 'noopener,noreferrer');
  };
  return (
    <NoticePanel
      tone="warning"
      hideIndicator
      title={t('settings.subscription.paymentFailure.title')}
      m={0}
      w="full"
      borderRadius={0}
      py={1.5}
      gap={2}
      actions={
        canOpen || canContactSupport ? (
          <Flex gap={2} flexWrap="wrap" w="full">
            {canContactSupport && (
              <ThemedButton asChild variant="secondary" size="xs" w={{ base: 'full', sm: 'auto' }}>
                <a href="mailto:support@fluenceur.com">
                  {t('settings.subscription.paymentRecovery.contactSupport')}
                </a>
              </ThemedButton>
            )}
            {canOpen && (
              <ThemedButton
                variant="secondary"
                size="xs"
                w={{ base: 'full', sm: 'auto' }}
                loading={stripe && portal.isPending}
                disabled={
                  billing.isFetching || (stripe && (settings.isFetching || portal.isPending))
                }
                onClick={openBilling}
              >
                {t(
                  stripe
                    ? 'settings.subscription.paymentFailure.stripeAction'
                    : 'settings.subscription.paymentFailure.updatePayment'
                )}
              </ThemedButton>
            )}
          </Flex>
        ) : undefined
      }
    >
      <Stack gap={0.5}>
        <Text>{t(body)}</Text>
        {stripe && recovery && (
          <>
            {recovery.status === 'retrying' && recovery.nextRetryAt && (
              <Text>
                {t('settings.subscription.paymentRecovery.nextRetry', {
                  date: formatSettingsDate(recovery.nextRetryAt, t('common.locale')),
                })}
              </Text>
            )}
            <Flex gapX={4} gapY={1} flexWrap="wrap">
              {recovery.invoices.map((invoice) => (
                <Text key={invoice.id}>
                  {t(
                    invoice.number
                      ? 'settings.subscription.paymentRecovery.invoice'
                      : 'settings.subscription.paymentRecovery.invoiceWithoutNumber',
                    {
                      number: invoice.number,
                      amount: formatCents(
                        invoice.amountRemaining,
                        invoice.currency,
                        t('common.locale')
                      ),
                      currency: invoice.currency,
                    }
                  )}
                </Text>
              ))}
              {recovery.disputes?.map((dispute) => (
                <Text key={`dispute:${dispute.id}`}>
                  {t(
                    dispute.number
                      ? 'settings.subscription.paymentRecovery.disputeInvoice'
                      : 'settings.subscription.paymentRecovery.disputeInvoiceWithoutNumber',
                    {
                      number: dispute.number,
                      amount: formatCents(
                        dispute.amountRemaining,
                        dispute.currency,
                        t('common.locale')
                      ),
                      currency: dispute.currency,
                    }
                  )}
                </Text>
              ))}
            </Flex>
            {settings.error && <Text>{t('billing.settings.readError')}</Text>}
          </>
        )}
      </Stack>
    </NoticePanel>
  );
}
